import { Button, FoodsIcon, FoodsIconButton, FoodsTooltip, Typography } from "@foodpilot/foods";
import { Dialog, DialogActions, DialogContent, DialogTitle, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { CsrIndicatorForm } from "../EditPillarForm/PillarForm.utils";
import { IndicatorFields } from "../EditPillarForm/Pages/IndicatorFields";
import { canSaveLever } from "./UpsertDialog.utils";

export type LeverDialogState =
    | { mode: "create"; indicator: CsrIndicatorForm }
    | { mode: "edit"; indicator: CsrIndicatorForm };

type LeverUpsertDialogProps = {
    open: boolean;
    state: LeverDialogState;
    isSaving: boolean;
    onClose: () => void;
    onSave: (indicator: CsrIndicatorForm) => void;
};

export const LeverUpsertDialog = (props: LeverUpsertDialogProps) => {
    const { open, state, isSaving, onClose, onSave } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const [form, setForm] = useState(state.indicator);

    useEffect(() => {
        if (open) setForm(state.indicator);
    }, [open, state]);

    const isCreate = state.mode === "create";
    const canSave = canSaveLever(form);
    const closeDialog = () => {
        if (!isSaving) onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={closeDialog}
            fullWidth
            maxWidth="md"
            aria-labelledby="lever-upsert-dialog-title"
            slotProps={{ paper: { sx: { borderRadius: 2, maxHeight: `calc(100% - ${theme.spacing(4)})` } } }}
        >
            <DialogTitle
                id="lever-upsert-dialog-title"
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3.5, py: 2.5 }}
            >
                <Typography component="span" variant="big-bold">
                    {isCreate ?
                        t("settings.csr.lever_dialog.create_title", "Add a lever")
                    :   t("settings.csr.lever_dialog.edit_title", "Edit a lever")}
                </Typography>
                <FoodsIconButton
                    size="m"
                    icon="close"
                    buttonProps={{
                        "aria-label": t("settings.csr.lever_dialog.close", "Close lever dialog"),
                        disabled: isSaving,
                        onClick: closeDialog,
                    }}
                />
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
                <IndicatorFields indicator={form} setIndicator={setForm} />
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-between", px: 3.5, py: 2.5 }}>
                <Button
                    variant="text"
                    endIcon={<FoodsIcon icon="close" size={1.5} />}
                    disabled={isSaving}
                    onClick={closeDialog}
                >
                    {t("primarydialog.cancel", "Cancel")}
                </Button>
                <FoodsTooltip
                    title={
                        canSave ? "" : (
                            t(
                                "settings.csr.lever_dialog.required_fields_tooltip",
                                "All fields marked with * must be filled in order to save your lever.",
                            )
                        )
                    }
                >
                    <span>
                        <Button
                            variant="primary"
                            startIcon={<FoodsIcon icon="check" size={2} />}
                            disabled={!canSave || isSaving}
                            onClick={() => onSave(form)}
                        >
                            {t("glossary.save", "Save")}
                        </Button>
                    </span>
                </FoodsTooltip>
            </DialogActions>
        </Dialog>
    );
};
